import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown, Home, Plus } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import { useProperty } from '@/contexts/property-context';
import { useAuth } from '@/contexts/auth-context';
import { useMyWorkspaces } from '@/hooks/use-workspaces';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/common/Badge';
import { useListboxKeyboard } from '@/hooks/use-listbox-keyboard';

const EMPTY_LIST: never[] = [];

interface ResidentUnit {
  unit_id: string;
  unit_number: string;
  floor: number;
  square_meters?: number | null;
  bedrooms?: number | null;
  property_id: string;
  property_name: string;
  property_address: string;
  relationship?: 'owner' | 'renter';
  role?: string;
  role_in_unit?: string;
  current_balance: number;
  is_overdue: boolean;
}

/**
 * Property and Apartment context switcher for portal users (residents).
 * Lists the resident's properties by name,
 * and the resident's apartments in the active property.
 */
export const ResidentScopeSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    properties,
    selectedPropertyId,
    selectedProperty,
    setSelectedPropertyId,
    residentMode,
    selectedUnitId,
    setSelectedUnitId,
    activeWorkspaceId,
    isResidentScope,
  } = useProperty();

  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);

  const { data: memberships = EMPTY_LIST } = useMyWorkspaces();
  const activeMembership =
    memberships.find((m) => m.workspace_id === activeWorkspaceId) || memberships[0];

  const { appMode } = useAuth();
  const myUnitsScope = appMode === 'home' || residentMode === 'home' ? 'resident' : 'manage';
  const { data: myUnits = EMPTY_LIST } = useQuery<ResidentUnit[]>({
    queryKey: ['portal-my-units', myUnitsScope],
    queryFn: async () => {
      const res = await api.get(
        myUnitsScope === 'resident' ? '/portal/my-units?scope=resident' : '/portal/my-units'
      );
      return res.data;
    },
  });

  const scopedUnits = useMemo(() => {
    const list = selectedProperty
      ? myUnits.filter((u) => u.property_id === selectedProperty.id)
      : myUnits;
    return [...list].sort((a, b) =>
      a.unit_number.localeCompare(b.unit_number, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [selectedProperty, myUnits]);

  // Auto-sync selectedUnitId if invalid or not belonging to the active property
  useEffect(() => {
    if (scopedUnits.length > 0 && !scopedUnits.some((u) => u.unit_id === selectedUnitId)) {
      setSelectedUnitId(scopedUnits[0].unit_id);
    }
  }, [scopedUnits, selectedUnitId, setSelectedUnitId]);

  const unitRoleLabel = (u: ResidentUnit) => {
    const isOwner = u.relationship === 'owner' || u.role_in_unit === 'owner' || u.role === 'owner';
    return isOwner
      ? t('portal.home_role_owner', 'Owner')
      : t('portal.home_role_renter', 'Renter');
  };

  const activeUnit = scopedUnits.find((u) => u.unit_id === selectedUnitId) ?? null;
  const selectedUnitIndex = scopedUnits.findIndex((u) => u.unit_id === selectedUnitId);
  const selectedPropertyIndex = properties.findIndex((b) => b.id === selectedPropertyId);

  // In My-home mode the resident list is deliberately multi-PM (every property
  // where they hold a unit role), so it is never labelled with a single PM.
  // Driven by the real query scope, not residentMode: the two disagree while
  // the workspaces query is still resolving.
  const residentScope = isResidentScope;

  const {
    containerRef: propertyDropdownRef,
    activeIndex: activePropertyIndex,
    onTriggerKeyDown: onPropertyTriggerKeyDown,
    onMenuKeyDown: onPropertyMenuKeyDown,
  } = useListboxKeyboard({
    itemCount: properties.length,
    isOpen: propertyDropdownOpen,
    setIsOpen: setPropertyDropdownOpen,
    selectedIndex: selectedPropertyIndex,
    onSelect: (i) => {
      const next = properties[i];
      if (next) setSelectedPropertyId(next.id);
    },
  });

  const {
    containerRef: unitDropdownRef,
    activeIndex: activeUnitIndex,
    onTriggerKeyDown: onUnitTriggerKeyDown,
    onMenuKeyDown: onUnitMenuKeyDown,
  } = useListboxKeyboard({
    itemCount: scopedUnits.length,
    isOpen: unitDropdownOpen,
    setIsOpen: setUnitDropdownOpen,
    selectedIndex: selectedUnitIndex,
    onSelect: (i) => {
      const next = scopedUnits[i];
      if (next) setSelectedUnitId(next.unit_id);
    },
  });

  if (properties.length < 2 && scopedUnits.length <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      {/* Property Switcher */}
      {(properties.length >= 2 || (properties.length === 1 && scopedUnits.length > 1)) && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium tracking-[0.1em] text-muted-foreground/70 hidden sm:inline">
            {t('suspension.scope_label')}
          </span>
          <div className="relative" ref={propertyDropdownRef}>
            <button
              type="button"
              aria-haspopup={properties.length >= 2 ? 'listbox' : undefined}
              aria-expanded={properties.length >= 2 ? propertyDropdownOpen : undefined}
              aria-label={t('suspension.select_property')}
              onClick={() => {
                if (properties.length >= 2) {
                  setPropertyDropdownOpen((prev) => !prev);
                }
              }}
              onKeyDown={properties.length >= 2 ? onPropertyTriggerKeyDown : undefined}
              className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary cursor-pointer"
            >
              <Building2 size={13} className="text-primary shrink-0" />
              <span className="max-w-[140px] sm:max-w-[200px] truncate font-semibold">
                {selectedProperty?.name || t('suspension.select_property')}
              </span>
              {properties.length >= 2 && (
                <ChevronDown
                  size={12}
                  className={cn(
                    'text-muted-foreground transition-transform duration-200 shrink-0',
                    propertyDropdownOpen && 'rotate-180'
                  )}
                />
              )}
            </button>

            {properties.length >= 2 && propertyDropdownOpen && (
              <div className="absolute left-0 mt-1.5 w-64 rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                  {residentScope
                    ? t('suspension.my_properties')
                    : activeMembership
                    ? t('shell.workspace_properties', {
                        name: activeMembership.workspace_name,
                      })
                    : t('suspension.my_properties')}
                </div>
                <div
                  role="listbox"
                  tabIndex={-1}
                  aria-activedescendant={
                    activePropertyIndex >= 0 ? `property-option-${activePropertyIndex}` : undefined
                  }
                  onKeyDown={onPropertyMenuKeyDown}
                  className="max-h-48 overflow-y-auto space-y-0.5 py-1"
                >
                  {properties.map((b, i) => {
                    const isSelected = b.id === selectedPropertyId;
                    const isActive = i === activePropertyIndex;
                    return (
                      <button
                        key={b.id}
                        id={`property-option-${i}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          setSelectedPropertyId(b.id);
                          setPropertyDropdownOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer text-left',
                          isActive && !isSelected ? 'bg-secondary text-foreground' : '',
                          isSelected
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                      >
                        <div className="min-w-0 pr-2">
                          <p className="font-semibold text-foreground truncate flex items-center gap-1.5">
                            {b.name}
                            {b.status === 'suspended' && (
                              <Badge size="sm" variant="warning">
                                {t('suspension.suspended_suffix')}
                              </Badge>
                            )}
                          </p>
                          <p className="text-[10.5px] text-muted-foreground truncate">
                            {residentScope
                              ? b.management_company || b.city || b.address
                              : b.city || b.address}
                          </p>
                        </div>
                        {isSelected && <Check size={13} className="text-primary shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-border pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setPropertyDropdownOpen(false);
                      navigate('/properties');
                    }}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                  >
                    <Plus size={13} /> {t('nav.properties', 'Properties Directory')}
                  </button>
                </div>
              </div>
            )}
          </div>
          {selectedProperty?.status === 'suspended' && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-500">
              {t('suspension.view_only_label')}
            </span>
          )}
        </div>
      )}

      {/* Apartment Switcher */}
      {scopedUnits.length > 1 && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium tracking-[0.1em] text-muted-foreground/70 hidden sm:inline">
            {t('suspension.scope_unit')}
          </span>
          <div className="relative" ref={unitDropdownRef}>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={unitDropdownOpen}
              aria-label={t('suspension.select_unit')}
              onClick={() => setUnitDropdownOpen((prev) => !prev)}
              onKeyDown={onUnitTriggerKeyDown}
              className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary cursor-pointer"
            >
              <Home size={13} className="text-primary shrink-0" />
              <span className="max-w-[120px] sm:max-w-[180px] truncate font-semibold">
                {activeUnit
                  ? t('suspension.unit_option', {
                      unit: activeUnit.unit_number,
                      role: unitRoleLabel(activeUnit),
                    })
                  : t('suspension.select_unit')}
              </span>
              <ChevronDown
                size={12}
                className={cn(
                  'text-muted-foreground transition-transform duration-200 shrink-0',
                  unitDropdownOpen && 'rotate-180'
                )}
              />
            </button>

            {unitDropdownOpen && (
              <div className="absolute left-0 mt-1.5 w-64 rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                  {t('suspension.scope_unit')}
                </div>
                <div
                  role="listbox"
                  tabIndex={-1}
                  aria-activedescendant={
                    activeUnitIndex >= 0 ? `unit-option-${activeUnitIndex}` : undefined
                  }
                  onKeyDown={onUnitMenuKeyDown}
                  className="max-h-48 overflow-y-auto space-y-0.5 py-1"
                >
                  {scopedUnits.map((u, i) => {
                    const isSelected = u.unit_id === selectedUnitId;
                    const isActive = i === activeUnitIndex;
                    return (
                      <button
                        key={u.unit_id}
                        id={`unit-option-${i}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          setSelectedUnitId(u.unit_id);
                          setUnitDropdownOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer text-left',
                          isActive ? 'bg-secondary text-foreground' : '',
                          isSelected
                            ? 'text-primary font-semibold'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )}
                      >
                        <span className="min-w-0 pr-2 truncate">
                          {t('suspension.unit_option', {
                            unit: u.unit_number,
                            role: unitRoleLabel(u),
                          })}
                        </span>
                        {isSelected && <Check size={13} className="text-primary shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
